import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { BASE_PATH_2_QUERY, TEMPLATE_QUERY } from '@glass/common/modules/pages/queryParams';
import Box from '@glass/web/components/base/Box';
import Typography from '@glass/web/components/base/Typography';
import { CONTACT_SECTION } from '@glass/web/modules/resumes/router/sections';
import { CENTER } from '@glass/web/modules/theme/constants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import ButtonCenterHoverCta from '@glass/shared/components/Button/ButtonCenterHoverCta';
import calcImageSizes from '@glass/shared/components/Image/calcImageSizes';
import ResumeBuilderLink from '@glass/shared/components/Link/ResumeBuilderLink/ResumeBuilderLink';
import ResumeImage from '@glass/shared/components/ResumeBuilder/ResumeImage';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const imageSizes = calcImageSizes();

const SCALE = 'scale(1.025)';
const useStyles = makeStyles()((theme) => ({
  paper: {
    position: 'relative',
    overflow: 'hidden',
    transition: theme.transitions.create(['transform']),
    cursor: 'pointer',
    '&:hover': {
      transform: SCALE,
    },
  },
  subheader: {
    position: 'absolute',
    top: 0,
    right: 0,
    color: theme.palette.common.white,
    background: theme.palette.primary.light,
    transform: 'translate(29.4%, -100%) rotateZ(45deg)',
    transformOrigin: 'bottom left',
    padding: theme.spacing(0.5, 3),
    opacity: 0.95,
  },
  selected: {
    border: '1px solid',
    borderColor: theme.palette.primary.light,
    borderRadius: theme.shape.borderRadius,
  },
}));

const TRACKING_CONTEXT = 'resume-template';

const ResumeTemplate = React.memo(
  ({ slug, load, iSlug, jSlug, seed, subheader, name, selected, updatedAt, idx }) => {
    const { query: queryParams } = useRouter();

    const { classes, cx } = useStyles();
    const trackProps = useMemo(
      () => ({ action: TRACKING_CONTEXT, label: slug, index: idx }),
      [idx, slug],
    );

    const params = useMemo(
      () => ({
        ...queryParams, // forward all query params
        [BASE_PATH_2_QUERY]: CONTACT_SECTION,
        [TEMPLATE_QUERY]: slug,
      }),
      [queryParams, slug],
    );

    return (
      <ResumeBuilderLink params={params} trackProps={trackProps}>
        <ResumeImage
          className={cx(classes.paper, selected && classes.selected)}
          contentResumeSlug={iSlug || jSlug ? undefined : 'default'}
          iSlug={iSlug}
          jSlug={jSlug}
          load={load}
          seed={seed}
          sizes={imageSizes}
          template={slug}
          updatedAt={updatedAt}
        >
          {subheader ? (
            <Typography className={classes.subheader} color="textPrimary" display="inline">
              {subheader}
            </Typography>
          ) : null}
          <ButtonCenterHoverCta>Choose</ButtonCenterHoverCta>
        </ResumeImage>
        <Box my={1}>
          <Typography align={CENTER} color="textPrimary" template={slug} variant="h6">
            {name}
          </Typography>
        </Box>
      </ResumeBuilderLink>
    );
  },
);

ResumeTemplate.propTypes = {
  idx: PropTypes.number,
  iSlug: PropTypes.string,
  jSlug: PropTypes.string,
  load: PropTypes.bool,
  name: PropTypes.string,
  seed: PropTypes.string,
  selected: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  slug: PropTypes.string,
  subheader: PropTypes.string,
  updatedAt: PropTypes.string,
};

export default React.memo(withTrackingContext(ResumeTemplate, TRACKING_CONTEXT));
